import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Menu.css";

export type MenuPlacement =
  | "bottom-start"
  | "bottom-end"
  | "top-start"
  | "top-end"
  | "right-start"
  | "left-start";

export type MenuItem = {
  key: string;
  label: React.ReactNode;
  description?: string;
  icon?: React.ReactNode;
  className?: string;
  onSelect?: () => void;
  selected?: boolean;
  disabled?: boolean;
  destructive?: boolean;
  tone?: "default" | "danger";
  closeOnSelect?: boolean;
};

export type MenuHeader = {
  backLabel?: string;
  onBack?: () => void;
  title?: string;
  onTitleClick?: () => void;
  backIcon?: React.ReactNode;
  titleIcon?: React.ReactNode;
};

type MenuProps = {
  trigger: React.ReactNode;
  items: MenuItem[];
  placement?: MenuPlacement;
  header?: MenuHeader;
  maxHeight?: number | string;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  dropdownClassName?: string;
  closeOnSelect?: boolean;
  allowNoSelection?: boolean;
  matchTriggerWidth?: boolean;
  showSelectionIndicator?: boolean;
  usePortal?: boolean;
  portalContainer?: HTMLElement | null;
  customContent?: React.ReactNode;
  open?: boolean;
  style?: React.CSSProperties;
};

const Menu: React.FC<MenuProps> = ({
  trigger,
  items,
  placement = "bottom-start",
  header,
  maxHeight = "80vh",
  onOpenChange,
  className,
  dropdownClassName,
  closeOnSelect = true,
  allowNoSelection = true,
  matchTriggerWidth = true,
  showSelectionIndicator = false,
  usePortal = false,
  portalContainer,
  customContent,
  open: controlledOpen,
  style,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState<number>();
  const [portalStyle, setPortalStyle] = useState<CSSProperties>();
  const isControlled = controlledOpen !== undefined;
  const actualOpen = isControlled ? Boolean(controlledOpen) : open;

  const hasSelected = useMemo(() => items.some(item => item.selected), [items]);

  const setOpenState = (next: boolean) => {
    if (!isControlled) setOpen(next);
    onOpenChange?.(next);
  };

  const toggleOpen = () => {
    setOpenState(!actualOpen);
  };

  const closeMenu = () => {
    setOpenState(false);
  };

  const updateMetrics = () => {
    const node = containerRef.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    if (!usePortal && matchTriggerWidth) {
      setTriggerWidth(rect.width);
    }

    if (usePortal) {
      setPortalStyle({
        position: "absolute",
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: matchTriggerWidth ? rect.width : undefined,
      });
    }
  };

  useEffect(() => {
    if (!actualOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    updateMetrics();
    window.addEventListener("resize", updateMetrics);
    window.addEventListener("scroll", updateMetrics, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", updateMetrics);
      window.removeEventListener("scroll", updateMetrics, true);
    };
  }, [actualOpen, matchTriggerWidth, usePortal]);

  useEffect(() => {
    if (!matchTriggerWidth || usePortal) return;
    updateMetrics();
  }, [matchTriggerWidth, usePortal]);

  useEffect(() => {
    if (isControlled) {
      setOpen(Boolean(controlledOpen));
    }
  }, [controlledOpen, isControlled]);

  const handleSelect = (item: MenuItem) => {
    if (item.disabled) return;
    item.onSelect?.();
    const shouldClose = item.closeOnSelect ?? closeOnSelect;
    if (shouldClose) closeMenu();
  };

  const renderTrigger = () => {
    if (React.isValidElement(trigger)) {
      const triggerElement = trigger as React.ReactElement<any>;
      const isDisabled = Boolean(triggerElement.props?.disabled);

      return React.cloneElement(triggerElement, {
        onClick: (event: React.MouseEvent) => {
          triggerElement.props.onClick?.(event);
          if (isDisabled) return;
          event.preventDefault();
          toggleOpen();
        },
        "aria-expanded": isDisabled ? false : actualOpen,
        "aria-haspopup": 'menu',
      });
    }

    return (
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={actualOpen}
        aria-haspopup="menu"
        className="menu-trigger"
      >
        {trigger}
      </button>
    );
  };

  const dropdownClasses = [
    "menu-dropdown",
    `menu-dropdown-${placement}`,
    dropdownClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const baseDropdownStyle: CSSProperties = {
    maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
  };

  const inlineWidthStyle: CSSProperties = !usePortal && matchTriggerWidth && triggerWidth
    ? { width: `${triggerWidth}px` }
    : {};

  const dropdownStyle = usePortal
    ? { ...(portalStyle || {}), ...baseDropdownStyle }
    : { ...inlineWidthStyle, ...baseDropdownStyle };

  const dropdownContent = (
    <div className={dropdownClasses} ref={dropdownRef} role="menu" style={dropdownStyle}>
      {customContent && <div className="menu-custom-content">{customContent}</div>}
      {header && (
        <div className="menu-header">
          {header.backLabel && header.onBack && (
            <button
              type="button"
              className="menu-header-back"
              onClick={() => {
                header.onBack?.();
                closeMenu();
              }}
            >
              {header.backIcon && <span className="menu-header-icon">{header.backIcon}</span>}
              <span>{header.backLabel}</span>
            </button>
          )}
          {header.title && (
            <button
              type="button"
              className="menu-header-title"
              onClick={() => {
                header.onTitleClick?.();
                closeMenu();
              }}
              disabled={!header.onTitleClick}
            >
              {header.titleIcon && <span className="menu-header-icon">{header.titleIcon}</span>}
              <span>{header.title}</span>
            </button>
          )}
        </div>
      )}
      <div className="menu-items">
        {items.map(item => (
          <button
            key={item.key}
            type="button"
            className={[
              "menu-item",
              item.className || "",
              item.disabled ? "disabled" : "",
              item.selected && (allowNoSelection || hasSelected) ? "selected" : "",
              item.destructive ? "destructive" : "",
              item.tone && item.tone !== "default" ? `tonal-${item.tone}` : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => handleSelect(item)}
            disabled={item.disabled}
            role="menuitem"
          >
            {item.icon && <span className="menu-item-icon">{item.icon}</span>}
            <span className="menu-item-body">
              <span className="menu-item-label">{item.label}</span>
              {item.description && (
                <span className="menu-item-description">{item.description}</span>
              )}
            </span>
            {showSelectionIndicator && item.selected && (allowNoSelection || hasSelected) && (
              <span className="menu-item-selected-indicator" aria-hidden="true">
                <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M13 1L5 9L1 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div
      className={["menu-container", className].filter(Boolean).join(" ")}
      ref={containerRef}
      style={{
        ...(style || {}),
        ["--menu-check-icon" as any]: `url(${process.env.PUBLIC_URL}/assets/nav/selected.svg)`,
      }}
    >
      {renderTrigger()}
      {actualOpen && (
        usePortal
          ? (portalStyle ? createPortal(dropdownContent, portalContainer || document.body) : null)
          : dropdownContent
      )}
    </div>
  );
};

export default Menu;
